import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Field, FieldProps } from "formik";
import {
  ACCOUNTING_METHODS_OPTIONS,
  AVAILABLE_REPORTS,
  CUSTOM_REPORT,
  EXPORT_FORMAT,
  BALANCE_SHEET,
  INCOME_STATEMENT,
  REVENUE_RECOGNITION,
  ExportFormatType,
  CASH_FLOW_STATEMENT,
  AR_AGING_SUMMARY,
  AP_AGING_SUMMARY,
  DOWNLOAD_FORMAT,
  TRIAL_BALANCE,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import dayjs, { Dayjs } from "dayjs";
import { useState, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import classNames from "classnames";
import { pluralize } from "utils/pluralize";
import { useQuery } from "hooks/useQuery";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { PlusIcon } from "components/icons/PlusIcon";
import ToolTip from "components/design/toolTip";
import { EditPencil } from "components/icons/EditPencil";
import { useModal } from "hooks/useModal";
import {
  ExportFormValues,
  FinancialStatementReportItem,
  AgingSummaryReportItem,
  RevenueRecognitionReportItem,
  ReportItem,
  CustomReportItem,
} from "types/Models/consolidatedReport";
import {
  isFinancialStatementReport,
  isAgingSummaryReport,
  isRevenueRecognitionReport,
  getReportDisabledState,
  removeDisabledReports,
  isTrialBalanceReport,
  hasAvailableGroupByOptions,
  addDefaultGroupBy,
  isCustomReport,
  hasAvailableGroupByOptionsForCustomReport,
} from "utils/allReports";
import { REPORTS_WITH_GROUP_BY } from "constants/bookkeeping";
import { CURRENT_TAX_YEAR } from "constants/dateFilter";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { MultiValue, SingleValue } from "react-select";
import { DateRangeValue, getDateRangeWithSetting } from "utils/getDateRange";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { useToast } from "hooks/useToast";
import { TrashSimple } from "components/icons/TrashSimple";
import { DragIcon } from "components/icons/DragIcon";
import { getDatePeriod, getDatePeriodWithSetting } from "utils/getDatePeriod";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import {
  useExportConsolidatedReportMutation,
  ConsolidatedReportDownloadRequest,
} from "store/apis/reports";
import { useGetCustomReportsQuery } from "store/apis/customReports";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { AllReportModals } from "../ReportModals/AllReportModals";
import {
  DEFAULT_EXPORT_ANALYSIS_TYPES,
  DEFAULT_VISIBLE_SECTIONS,
  DEFAULT_GROUP_ORDER,
  ExportAnalysisTypes,
  SectionKey,
} from "constants/financialSummary";
import { IncludeFinancialSummary } from "./IncludeFinancialSummary";
import { FinancialSummaryPrint } from "pages/Books/FinancialsSummary/FinancialSummaryPrint";
import {
  FinancialMetrics,
  useGetFinancialMetricsQuery,
} from "store/apis/financialMetrics";

type FinancialSummaryPrintConfig = {
  filter: { START_DATE: string; END_DATE: string };
  financialMetrics: FinancialMetrics | undefined;
  exportSections: Record<SectionKey, boolean>;
  groupOrder: string[];
  periodText: string;
  groupId: string;
  isCustomDateRange?: boolean;
  analysisTypes: ExportAnalysisTypes;
  onCaptured?: (base64Images: string[]) => void;
};

type ExportConsolidatedReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  groupId: string;
};

type DraggableReportItemProps = {
  item: ReportItem;
  index: number;
  exportFormat: string;
  onSave: (updatedReport: ReportItem, index: number) => void;
  onDelete: (index: number) => void;
  moveItem: (dragIndex: number, dropIndex: number) => void;
  entityId: string;
  selectedReports: ReportItem[];
};

export const ExportButton = ({
  exportReports,
  onSaveInInkleDocs,
  isExporting,
  disabled,
}: {
  exportReports: (isEmail: boolean) => void;
  onSaveInInkleDocs: () => void;
  isExporting: boolean;
  disabled: boolean;
}) => (
  <Dropdown.Root>
    <Dropdown.Trigger className="t-group" asChild disabled={disabled}>
      <div>
        <Button
          customType="primary"
          onClick={(e) => e.stopPropagation()}
          isLoading={isExporting}
          disabled={disabled}
        >
          Export
          <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
            <ArrowDown strokeWidth={"1.5"} color="currentColor" />
          </div>
        </Button>
      </div>
    </Dropdown.Trigger>
    <Dropdown.Portal>
      <Dropdown.Content align="start" sideOffset={4}>
        <ExportOptions
          onLocalDownload={() => exportReports(false)}
          onMailExport={() => exportReports(true)}
          onSaveInInkleDocs={onSaveInInkleDocs}
          recommendedOptionText="Recommended since it is a large file & local downloads may timeout."
        />
      </Dropdown.Content>
    </Dropdown.Portal>
  </Dropdown.Root>
);

const AddReportDropdown = ({
  onAddReport,
  exportFormat,
  accountingMethod,
  selectedReports,
  entityId,
}: {
  onAddReport: (report: ReportItem) => void;
  exportFormat: string;
  accountingMethod: string;
  selectedReports: ReportItem[];
  entityId: string;
}) => {
  const addModal = useModal();
  const { successToast } = useToast();
  const [pendingReport, setPendingReport] = useState<ReportItem | null>(null);

  const { data: customReports = [] } = useGetCustomReportsQuery({ entityId });

  const isExcelFormat = exportFormat === EXPORT_FORMAT.EXCEL;

  const availableReports = AVAILABLE_REPORTS.filter((report) => {
    const isAlreadyAdded = selectedReports.some(
      (item) => item.name === report.label
    );

    if (!isAlreadyAdded) return true;

    const hasGroupBy = REPORTS_WITH_GROUP_BY.includes(
      report.value as (typeof REPORTS_WITH_GROUP_BY)[number]
    );

    if (!isExcelFormat || !hasGroupBy) return false;

    return hasAvailableGroupByOptions(report.value, selectedReports);
  });

  const availableCustomReports = customReports.filter((report) => {
    if (report.is_template) return false;

    const isAlreadyAdded = selectedReports.some(
      (item) => isCustomReport(item) && item.custom_report_id === report.uuid
    );

    if (!isAlreadyAdded) return true;

    return (
      isExcelFormat &&
      hasAvailableGroupByOptionsForCustomReport(report.uuid, selectedReports)
    );
  });

  const hasNoReports =
    availableReports.length === 0 && availableCustomReports.length === 0;

  const handleSelectReport = (report: { label: string; value: string }) => {
    let baseReport = {
      id: crypto.randomUUID(),
      name: report.label,
      value: report.value,
    } as ReportItem;

    if (
      isFinancialStatementReport(baseReport) ||
      isTrialBalanceReport(baseReport)
    ) {
      baseReport = {
        id: crypto.randomUUID(),
        name: report.label,
        value: report.value,
        groupBy: "",
        groupByLabel: "None",
      } as ReportItem;
    }

    const isExcelFormat = exportFormat === EXPORT_FORMAT.EXCEL;
    const requiresModal =
      isExcelFormat &&
      (isFinancialStatementReport(baseReport) ||
        isAgingSummaryReport(baseReport) ||
        isRevenueRecognitionReport(baseReport) ||
        isTrialBalanceReport(baseReport));

    if (requiresModal) {
      setPendingReport(baseReport);
      addModal.open();
    } else {
      onAddReport(baseReport);
      successToast({ message: `${baseReport.name} added successfully` });
    }
  };

  const handleSelectCustomReport = (report: {
    uuid: string;
    title: string;
  }) => {
    const newReport: ReportItem = {
      id: crypto.randomUUID(),
      name: report.title,
      value: CUSTOM_REPORT,
      custom_report_id: report.uuid,
      groupBy: "",
      groupByLabel: "None",
    };

    if (isExcelFormat) {
      setPendingReport(newReport);
      addModal.open();
    } else {
      onAddReport(newReport);
      successToast({ message: `${report.title} added successfully` });
    }
  };

  return (
    <>
      <Dropdown.Root modal={false}>
        <ConditionalToolTip condition={hasNoReports && "All reports added"}>
          <div>
            <Dropdown.Trigger asChild>
              <Button
                customType="secondary"
                size="small"
                leftIcon={<PlusIcon size="12" />}
                disabled={hasNoReports}
              >
                Add Reports
              </Button>
            </Dropdown.Trigger>
          </div>
        </ConditionalToolTip>
        <Dropdown.Content align="start" sideOffset={4} className="t-max-h-96">
          {availableReports.map((report) => {
            const { disabled, tooltip } = getReportDisabledState(
              report.value,
              exportFormat as ExportFormatType,
              accountingMethod
            );

            const item = (
              <Dropdown.Item
                key={report.value}
                onClick={() => !disabled && handleSelectReport(report)}
                disabled={disabled}
              >
                {report.label}
              </Dropdown.Item>
            );

            if (disabled && tooltip) {
              return (
                <ToolTip key={report.value} text={tooltip} align="end">
                  {item}
                </ToolTip>
              );
            }

            return item;
          })}
          {availableCustomReports.length > 0 && (
            <>
              <Dropdown.Separator />
              <Dropdown.Label>Custom Reports</Dropdown.Label>
              {availableCustomReports.map((report) => (
                <Dropdown.Item
                  key={report.uuid}
                  onClick={() => handleSelectCustomReport(report)}
                >
                  {report.title}
                </Dropdown.Item>
              ))}
            </>
          )}
        </Dropdown.Content>
      </Dropdown.Root>
      {pendingReport && (
        <AllReportModals
          isOpen={addModal.isOpen}
          onClose={addModal.close}
          report={pendingReport}
          isEditMode={false}
          onSave={(updatedReport) => {
            onAddReport(updatedReport);
            successToast({
              message: `${updatedReport.name} added successfully`,
            });
            addModal.close();
          }}
          entityId={entityId}
          selectedReports={selectedReports}
        />
      )}
    </>
  );
};

const ReportItemActions = ({
  item,
  index,
  exportFormat,
  onSave,
  onDelete,
  dragHandleRef,
  entityId,
  selectedReports,
}: {
  item: ReportItem;
  index: number;
  exportFormat: string;
  onSave: (updatedReport: ReportItem, index: number) => void;
  onDelete: (index: number) => void;
  dragHandleRef: React.RefObject<HTMLButtonElement | null>;
  entityId: string;
  selectedReports: ReportItem[];
}) => {
  const editModal = useModal();
  const isExcelFormat = exportFormat === EXPORT_FORMAT.EXCEL;
  const canEdit =
    isExcelFormat &&
    (isFinancialStatementReport(item) ||
      isAgingSummaryReport(item) ||
      isRevenueRecognitionReport(item) ||
      isTrialBalanceReport(item) ||
      isCustomReport(item));

  return (
    <>
      <div className="t-flex t-items-center t-gap-0.5">
        {canEdit && (
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={editModal.open}
          >
            <EditPencil />
          </Button>
        )}
        <Button
          customType="ghost_icon"
          size="xsmall"
          onClick={() => onDelete(index)}
        >
          <TrashSimple />
        </Button>

        <ToolTip text="Rearrange sequence">
          <div className="!t-cursor-move t-text-text-30">
            <Button customType="ghost_icon" size="xsmall" ref={dragHandleRef}>
              <DragIcon />
            </Button>
          </div>
        </ToolTip>
      </div>
      {canEdit && (
        <AllReportModals
          isOpen={editModal.isOpen}
          onClose={editModal.close}
          report={item}
          isEditMode={true}
          onSave={(updatedReport) => {
            onSave(updatedReport, index);
            editModal.close();
          }}
          entityId={entityId}
          selectedReports={selectedReports.filter((r) => r.id !== item.id)}
        />
      )}
    </>
  );
};

const DraggableReportItem = ({
  item,
  index,
  exportFormat,
  onSave,
  onDelete,
  moveItem,
  entityId,
  selectedReports,
}: DraggableReportItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLButtonElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "report-item",
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "report-item",
      drop: (draggedItem: { index: number }) => {
        const dragIndex = draggedItem.index;
        const dropIndex = index;

        if (dragIndex !== dropIndex) {
          moveItem(dragIndex, dropIndex);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [index, moveItem]
  );

  drag(drop(ref));

  const renderExcelSubText = (item: ReportItem) => {
    switch (item.value) {
      case TRIAL_BALANCE:
      case BALANCE_SHEET:
      case INCOME_STATEMENT:
      case CASH_FLOW_STATEMENT: {
        const { groupBy, groupByLabel, classIds } =
          item as FinancialStatementReportItem;
        const hasGrouping = groupBy || (classIds && classIds.length > 0);
        return hasGrouping ? `Group by: ${groupByLabel}` : `Group by: None`;
      }

      case AR_AGING_SUMMARY:
      case AP_AGING_SUMMARY: {
        const { noOfDays, noOfPeriods } = item as AgingSummaryReportItem;
        return `Days per aging period: ${noOfDays}, Aging period: ${noOfPeriods}`;
      }

      case REVENUE_RECOGNITION: {
        const { groupBy, groupByLabel, showDeferrals, includeCarryForward } =
          item as RevenueRecognitionReportItem;

        return [
          groupBy ? `Group by: ${groupByLabel}` : null,
          showDeferrals ? "Include deferrals" : null,
          includeCarryForward ? "Include carry forward" : null,
        ]
          .filter(Boolean)
          .join(", ");
      }

      case CUSTOM_REPORT: {
        const { groupBy, groupByLabel } = item as CustomReportItem;
        return groupBy ? `Group by: ${groupByLabel}` : `Group by: None`;
      }
    }
  };

  const reportDescription = renderExcelSubText(item);

  return (
    <div className={classNames("t-flex t-flex-row t-items-center t-gap-2")}>
      <div className="t-text-text-60 t-text-body-sm t-w-2.5">{index + 1}.</div>
      <div
        ref={ref}
        className={classNames(
          "t-flex t-items-center t-justify-between t-py-3 t-px-4 t-border t-border-solid t-border-neutral-10 t-rounded t-w-full t-cursor-grab",
          {
            "t-opacity-50 !t-cursor-grabbing": isDragging,
            "t-border-neutral-20 t-bg-surface-lighter-grey": isOver,
          }
        )}
      >
        <div className="t-flex t-items-center">
          <div className="t-flex t-flex-col  t-gap-1">
            <span className="t-text-subtext t-text-text-100">{item.name}</span>
            <span className="t-text-body-sm-italic t-text-text-30 t-italic">
              {exportFormat === EXPORT_FORMAT.EXCEL && reportDescription}
            </span>
          </div>
        </div>

        <ReportItemActions
          item={item}
          index={index}
          exportFormat={exportFormat}
          onSave={onSave}
          onDelete={onDelete}
          dragHandleRef={dragHandleRef}
          entityId={entityId}
          selectedReports={selectedReports}
        />
      </div>
    </div>
  );
};

const ExportConsolidatedContent = ({
  entityId,
  groupId,
  onClose,
  onSaveToInkleDocs,
  onTriggerFinancialSummaryPrint,
}: {
  entityId: string;
  groupId: string;
  onClose: () => void;
  onSaveToInkleDocs: (documentId: string) => void;
  onTriggerFinancialSummaryPrint: (config: FinancialSummaryPrintConfig) => void;
}) => {
  const { values, setFieldValue, resetForm } =
    useTypedFormikContext<ExportFormValues>();
  const { successToast, alertToast } = useToast();
  const selectFolderModal = useModal();
  const [exportConsolidatedReport, { isLoading: isExporting }] =
    useExportConsolidatedReportMutation();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const { data: financialMetrics } = useGetFinancialMetricsQuery(
    {
      entityId,
      start_date: values.fromDate,
      end_date: values.toDate,
      is_custom_date_range: true,
    },
    { skip: !entityId || !values.includeFinancialSummary }
  );

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const [isCapturingGraphs, setIsCapturingGraphs] = useState(false);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleExport = (isEmail?: boolean, folderId?: string) => {
    if (
      values.includeFinancialSummary &&
      values.exportFormat !== EXPORT_FORMAT.EXCEL
    ) {
      setIsCapturingGraphs(true);
      const periodMonths =
        dayjs(values.toDate).diff(dayjs(values.fromDate), "month") + 1;
      const periodText = pluralize(periodMonths, "month", "months");

      onTriggerFinancialSummaryPrint({
        filter: { START_DATE: values.fromDate, END_DATE: values.toDate },
        financialMetrics,
        exportSections: values.financialSummarySections,
        groupOrder: values.financialSummaryGroupOrder,
        periodText,
        groupId,
        isCustomDateRange: true,
        analysisTypes: values.financialSummaryAnalysisTypes,
        onCaptured: (graphs: string[]) => {
          processExport(graphs, isEmail, folderId).finally(() =>
            setIsCapturingGraphs(false)
          );
        },
      });
    } else {
      processExport([], isEmail, folderId);
    }
  };

  const processExport = async (
    graphs: string[],
    isEmail?: boolean,
    folderId?: string
  ) => {
    try {
      const isExcelFormat = values.exportFormat === EXPORT_FORMAT.EXCEL;

      const download_requests: ConsolidatedReportDownloadRequest[] =
        values.contents.map((item) => {
          const request: ConsolidatedReportDownloadRequest = {
            report_type: item.value,
          };

          if (!isExcelFormat) {
            if (isCustomReport(item)) {
              request.custom_report_id = item.custom_report_id;
            }
            return request;
          }

          if (isFinancialStatementReport(item)) {
            if (item.classIds?.length === 0) {
              request.comparison_report_type = item.groupBy || null;
            } else {
              request.class_ids = item.classIds?.length ? item.classIds : null;
            }
          }

          if (isTrialBalanceReport(item)) {
            request.comparison_report_type = item.groupBy || null;
          }

          if (isAgingSummaryReport(item)) {
            request.days_per_aging_period = item.noOfDays;
            request.number_of_periods = item.noOfPeriods;
          }

          if (isRevenueRecognitionReport(item)) {
            request.comparison_report_type = item.groupBy || null;
            request.show_deferrals = item.showDeferrals;
            request.include_carry_forward = item.includeCarryForward;
          }

          if (isCustomReport(item)) {
            request.custom_report_id = item.custom_report_id;
            request.comparison_report_type = item.groupBy || null;
          }

          return request;
        });

      const payload = {
        start_date: dayjs(values.fromDate).format(YYYY_MM_DD),
        end_date: dayjs(values.toDate).format(YYYY_MM_DD),
        accounting_method: values.accountingMethod,
        show_non_zero_accounts: true,
        action: isExcelFormat
          ? DOWNLOAD_FORMAT.DOWNLOAD_EXCEL
          : DOWNLOAD_FORMAT.DOWNLOAD_PDF,
        download_requests,
        export_to_mail: isEmail,
        ...(folderId && { folder_id: folderId }),
        ...(graphs.length > 0 && {
          financial_summary_graphs: graphs,
        }),
      };

      const { document_id } = await exportConsolidatedReport({
        entityId,
        payload,
      }).unwrap();

      if (folderId) {
        onSaveToInkleDocs(document_id!);
      } else if (isEmail) {
        successToast({ message: "Report sent to your email" });
      } else {
        const { download_url } = await getPreviewUrl({
          groupId: groupId,
          fileId: document_id,
        }).unwrap();

        openLink(download_url);
        successToast({ message: "Report downloaded" });
      }
      resetForm();
      onClose();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const moveItem = (dragIndex: number, dropIndex: number) => {
    const newContents = [...values.contents];
    const [draggedItem] = newContents.splice(dragIndex, 1);
    newContents.splice(dropIndex, 0, draggedItem);
    setFieldValue("contents", newContents);
  };

  const handleAddReport = (report: ReportItem) => {
    setFieldValue("contents", [...values.contents, report]);
  };

  const handleSaveReport = (updatedReport: ReportItem, index: number) => {
    const updatedContents = [...values.contents];
    updatedContents[index] = updatedReport;
    setFieldValue("contents", updatedContents);
  };

  const handleDeleteReport = (index: number) => {
    const newContents = values.contents.filter((_, i) => i !== index);
    setFieldValue("contents", newContents);
  };

  const handlePeriodChange = (
    selectedValue: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (selectedValue instanceof Array) {
      return;
    }

    if (selectedValue?.value) {
      setFieldValue("period", selectedValue.value);

      if (selectedValue?.value === "CUSTOM") {
        return;
      }
    }

    const { startDate, endDate } = getDateRangeWithSetting(
      (selectedValue as SingleValue<OptionData>)?.value as DateRangeValue,
      bookkeepingSettings?.tax_year_end,
      bookkeepingStartDate
    );

    setFieldValue("fromDate", startDate?.format(YYYY_MM_DD) || "");
    setFieldValue("toDate", endDate?.format(YYYY_MM_DD) || "");
  };

  const periodList = getDatePeriodWithSetting(
    [...getDatePeriod, { label: "Custom", value: "CUSTOM" }],
    bookkeepingSettings?.tax_year_end,
    bookkeepingStartDate
  );
  return (
    <>
      <Modal.Header>
        <Modal.Title>Export Consolidated Report</Modal.Title>
        <Modal.Close />
      </Modal.Header>

      <Modal.Body>
        <div className="t-flex t-flex-col t-gap-6">
          <div>
            <Label className="t-mb-2" required>
              Export format
            </Label>
            <Radio.Root
              value={values.exportFormat}
              onValueChange={(value) => {
                setFieldValue("exportFormat", value as ExportFormatType);
                const filteredContents = removeDisabledReports(
                  values.contents,
                  value as ExportFormatType,
                  values.accountingMethod
                );
                setFieldValue("contents", filteredContents);
              }}
            >
              <Radio.Content>
                <div className="t-flex t-flex-row t-gap-x-4 sm:t-flex-wrap sm:t-gap-y-2">
                  <Radio.Item value={EXPORT_FORMAT.PDF} className="!t-w-auto">
                    <span className="t-text-body-sm t-text-text-100">PDF</span>
                  </Radio.Item>
                  <Radio.Item value={EXPORT_FORMAT.EXCEL} className="!t-w-auto">
                    <div className="t-flex t-items-center t-gap-2 sm:t-flex-wrap sm:t-gap-x-2 sm:t-gap-y-0">
                      <span className="t-text-body-sm t-text-text-100">
                        Excel
                      </span>
                      <span className="t-text-neutral-40 t-text-body-sm">
                        (Grouping options available)
                      </span>
                    </div>
                  </Radio.Item>
                </div>
              </Radio.Content>
            </Radio.Root>
          </div>

          <div className="t-grid t-grid-cols-2 t-gap-4 sm:t-grid-cols-1">
            <div>
              <Combobox
                withForm
                block
                value={ACCOUNTING_METHODS_OPTIONS.find(
                  (option) => option.value === values.accountingMethod
                )}
                required
                name="accountingMethod"
                label="Accounting method"
                options={ACCOUNTING_METHODS_OPTIONS}
                onChange={(selected) => {
                  if (selected && "value" in selected) {
                    setFieldValue("accountingMethod", selected.value);
                    const filteredContents = removeDisabledReports(
                      values.contents,
                      values.exportFormat,
                      selected.value
                    );
                    setFieldValue("contents", filteredContents);
                  }
                }}
                isClearable={false}
              />
            </div>

            <div>
              <Combobox
                name="period"
                label="Period"
                placeholder="Select period"
                components={{ ClearIndicator: () => null }}
                onChange={handlePeriodChange}
                options={periodList}
                block
                value={periodList.find((d) => d.value === values.period)}
                required
                menuPortalTarget={document.body}
                tooltip
                isOptionDisabled={(option) => Boolean(option.disableOption)}
              />
            </div>
          </div>

          <div className="t-grid t-grid-cols-2 t-gap-4 sm:t-grid-cols-1">
            <Field name="fromDate">
              {({ field }: FieldProps) => (
                <DateInput
                  {...field}
                  required
                  label="From"
                  portalId="fromDate"
                  onDateChange={(date) => {
                    setFieldValue("fromDate", dayjs(date).format(YYYY_MM_DD));
                    setFieldValue("period", "CUSTOM");
                  }}
                  maxDate={new Date(values.toDate)}
                  minDate={new Date(bookkeepingStartDate!)}
                />
              )}
            </Field>
            <Field name="toDate">
              {({ field }: FieldProps) => (
                <DateInput
                  {...field}
                  required
                  label="To"
                  portalId="toDate"
                  onDateChange={(date) => {
                    setFieldValue("toDate", dayjs(date).format(YYYY_MM_DD));
                    setFieldValue("period", "CUSTOM");
                  }}
                  minDate={new Date(values.fromDate)}
                  maxDate={new Date()}
                />
              )}
            </Field>
          </div>

          <div>
            <div className="t-flex t-flex-col t-gap-4 t-mt-3 t-mb-3">
              {values.exportFormat === EXPORT_FORMAT.PDF && (
                <IncludeFinancialSummary />
              )}

              {values.contents.length > 0 && (
                <span
                  className={classNames("t-text-body-sm t-text-text-30", {
                    "t-pl-4": values.exportFormat === EXPORT_FORMAT.PDF,
                  })}
                >
                  Financial Reports:
                </span>
              )}

              <div
                className={classNames("t-flex t-flex-col t-gap-4", {
                  "t-pl-4": values.exportFormat === EXPORT_FORMAT.PDF,
                })}
              >
                {values.contents.map((item, index) => (
                  <DraggableReportItem
                    key={item.id}
                    item={item}
                    index={index}
                    exportFormat={values.exportFormat}
                    onSave={handleSaveReport}
                    onDelete={handleDeleteReport}
                    moveItem={moveItem}
                    entityId={entityId}
                    selectedReports={values.contents}
                  />
                ))}
              </div>
              <div
                className={classNames("t-w-fit", {
                  "t-ml-5": !(values.contents.length === 0),
                })}
              >
                <AddReportDropdown
                  exportFormat={values.exportFormat}
                  accountingMethod={values.accountingMethod}
                  selectedReports={values.contents}
                  onAddReport={handleAddReport}
                  entityId={entityId}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.FooterButtonGroup>
        <Button
          customType="secondary"
          onClick={handleClose}
          disabled={isExporting}
        >
          Cancel
        </Button>
        <ExportButton
          exportReports={(isEmail) => handleExport(isEmail, undefined)}
          onSaveInInkleDocs={selectFolderModal.open}
          isExporting={isExporting || isCapturingGraphs}
          disabled={
            isExporting ||
            isCapturingGraphs ||
            (!values.includeFinancialSummary && values.contents.length === 0)
          }
        />
      </Modal.FooterButtonGroup>

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => {
            handleExport(false, folderId);
          }}
        />
      )}
    </>
  );
};

export const ExportConsolidatedReportModal = ({
  isOpen,
  onClose,
  entityId,
  groupId,
}: ExportConsolidatedReportModalProps) => {
  const query = useQuery();
  const savedToInkleDocsModal = useModal();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();
  const [financialSummaryPrintConfig, setFinancialSummaryPrintConfig] =
    useState<FinancialSummaryPrintConfig | null>(null);

  const accountingMethod = query.get("ACCOUNTING_METHOD")!;
  const period = query.get("SELECT_PERIOD") || CURRENT_TAX_YEAR;
  const startDate =
    query.get("START_DATE") || dayjs().startOf("year").format(YYYY_MM_DD);
  const endDate = query.get("END_DATE") || dayjs().format(YYYY_MM_DD);

  const initialValues: ExportFormValues = {
    exportFormat: EXPORT_FORMAT.PDF,
    accountingMethod,
    period,
    fromDate: startDate,
    toDate: endDate,
    contents: [
      {
        id: "1",
        name: "Balance Sheet",
        value: BALANCE_SHEET,
        groupBy: "",
        groupByLabel: "None",
      },
      {
        id: "2",
        name: "Income Statement",
        value: INCOME_STATEMENT,
        groupBy: "",
        groupByLabel: "None",
      },
    ],
    includeFinancialSummary: true,
    financialSummarySections: DEFAULT_VISIBLE_SECTIONS,
    financialSummaryGroupOrder: DEFAULT_GROUP_ORDER,
    financialSummaryAnalysisTypes: DEFAULT_EXPORT_ANALYSIS_TYPES,
  };

  const handleSaveToInkleDocs = (documentId: string) => {
    setSavedDocumentId(documentId);
    savedToInkleDocsModal.open();
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content size="large" useCustomOverlay asChild>
          <FormikForm
            enableReinitialize
            initialValues={initialValues}
            onSubmit={() => {}}
          >
            <ExportConsolidatedContent
              entityId={entityId}
              groupId={groupId}
              onClose={onClose}
              onSaveToInkleDocs={handleSaveToInkleDocs}
              onTriggerFinancialSummaryPrint={setFinancialSummaryPrintConfig}
            />
          </FormikForm>
        </Modal.Content>
      </Modal.Root>

      {financialSummaryPrintConfig && (
        <div className="t-fixed t-top-0 t-left-full t-w-[1200px] t-overflow-hidden t-pointer-events-none t-z-[-1] t-opacity-0">
          <FinancialSummaryPrint
            {...financialSummaryPrintConfig}
            onComplete={() => setFinancialSummaryPrintConfig(null)}
          />
        </div>
      )}

      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType="Consolidated Report"
        />
      )}
    </>
  );
};
